import axiosInstance from '../../config/axiosInstance';
import type {
  IngestSourceReport,
  KbSource,
  RetrievedChunk,
  SupportTicketDetail,
  TicketCategory,
  TicketPriority,
  TicketsPaginated,
  TicketsQueryFilters,
} from '../../types/support';

class SupportService {
  private readonly TICKETS = '/support/tickets';
  private readonly KB = '/support/kb';

  async getTickets(
    page = 1,
    pageSize = 20,
    filters?: TicketsQueryFilters,
  ): Promise<TicketsPaginated> {
    return axiosInstance.get(this.TICKETS, {
      params: {
        page,
        pageSize,
        // Empty strings are the "all" option in the toolbar selects; sending
        // them would fail the backend's enum validation.
        status: filters?.status || undefined,
        category: filters?.category || undefined,
        assignedTo: filters?.assignedTo || undefined,
        origin: filters?.origin || undefined,
      },
    });
  }

  async getTicket(id: string): Promise<SupportTicketDetail> {
    return axiosInstance.get(`${this.TICKETS}/${id}`);
  }

  async startChat(userId: string, text: string): Promise<SupportTicketDetail> {
    return axiosInstance.post(`${this.TICKETS}/start`, { userId, text });
  }

  async assignTicket(id: string): Promise<SupportTicketDetail> {
    return axiosInstance.post(`${this.TICKETS}/${id}/assign`);
  }

  async replyToTicket(id: string, text: string): Promise<SupportTicketDetail> {
    return axiosInstance.post(`${this.TICKETS}/${id}/reply`, { text });
  }

  async resolveTicket(id: string): Promise<SupportTicketDetail> {
    return axiosInstance.post(`${this.TICKETS}/${id}/resolve`);
  }

  async updateTicket(
    id: string,
    patch: { subject?: string; category?: TicketCategory; priority?: TicketPriority },
  ): Promise<SupportTicketDetail> {
    return axiosInstance.patch(`${this.TICKETS}/${id}`, patch);
  }

  async getKbSources(): Promise<KbSource[]> {
    return axiosInstance.get(`${this.KB}/sources`);
  }

  async createManualSource(name: string, text: string): Promise<IngestSourceReport> {
    return axiosInstance.post(`${this.KB}/sources`, { name, text });
  }

  async toggleSource(id: string, enabled: boolean): Promise<KbSource> {
    return axiosInstance.patch(`${this.KB}/sources/${id}`, { enabled });
  }

  /**
   * `lang` is deliberately not sent: retrieval does not filter by language, so
   * the field would only ever be a value the backend discards. `TestRetrievalDto`
   * keeps it optional with a default, so the API contract is unchanged.
   */
  async testRetrieval(query: string): Promise<{ chunks: RetrievedChunk[] }> {
    return axiosInstance.post(`${this.KB}/test-retrieval`, { query });
  }
}

export const supportService = new SupportService();
